import React, { useEffect, useState } from "react";
import "./PriceSlider.scss";
import { Slider, Tooltip } from "antd";
import { useUnit } from "contexts/UnitContext";
import Helpers from "@utils/Helpers";
import { InfoCircleOutlined } from "@ant-design/icons";

interface CustomStyle extends React.CSSProperties {
  "--slider-position-start"?: string;
  "--slider-position-end"?: string;
}

interface IPriceSlider {
  unit: number;
  productForm: any;
  type: "myProduct" | "createProduct";
  setProductForm: (form: any) => void;
  metrics?: any;
}

const PriceSlider: React.FC<IPriceSlider> = ({
  unit,
  type,
  productForm,
  metrics,
}) => {
  const { units, convertCurrency, convertWeight } = useUnit();
  const [bidValue, setBidValue] = useState([1200, 3600]);
  const percentageRangesStart = (bidValue[0] / 5000) * 100;
  const percentageRangesEnd = (bidValue[1] / 5000) * 100;
  const [startRangeWidth, setStartRangeWidth] = useState(0);
  const [endRangeWidth, setEndRangeWidth] = useState(0);
  const [marks, setMarks] = useState({});
  const [metricPrices, setMetricPrices] = useState({
    maxSliderPerTon: 0,
    minSliderPerTon: 0,
    average: 0,
    lowerPercentage: 0,
    upperPercentage: 0,
    percentageRangesStart: 0,
    percentageRangesEnd: 0,
  });

  useEffect(() => {
    const element: HTMLElement | null = document.querySelector(
      ".price-slider-component"
    );
    if (element) {
      const rect = element.getBoundingClientRect();
      const width = rect.width;
      const widthStart = (width / 100) * percentageRangesStart;
      const widthEnd = (width / 100) * (100 - percentageRangesEnd);
      setStartRangeWidth(widthStart);
      setEndRangeWidth(widthEnd);
    }
  }, [bidValue]);

  useEffect(() => {
    const max = convertCurrency(metrics?.max, "USD", units.currency);
    const mean = convertCurrency(metrics?.mean, "USD", units.currency);
    const min = convertCurrency(metrics?.min, "USD", units.currency);
    const q1 = convertCurrency(metrics?.q1, "USD", units.currency);
    const q2 = convertCurrency(metrics?.q2, "USD", units.currency);
    const q3 = convertCurrency(metrics?.q3, "USD", units.currency);

    const padding = min * 0.1;
    const adjustedMin = min - padding;
    const adjustedMax = max + padding;
    const range = adjustedMax - adjustedMin;

    const lowerPercentage = ((q1 - adjustedMin) / range) * 100;
    const upperPercentage = ((q3 - adjustedMin) / range) * 100;

    const percentageRangesStart = ((q2 - adjustedMin) / range) * 100;
    const percentageRangesEnd = percentageRangesStart; // If Q2 is a single value

    setMetricPrices({
      maxSliderPerTon: max,
      minSliderPerTon: min,
      average: mean,
      lowerPercentage: lowerPercentage,
      upperPercentage: upperPercentage,
      percentageRangesStart,
      percentageRangesEnd,
    });

    const newMarks = generateMarks([min, q1, q2, q3, max]);
    setMarks(newMarks);
  }, [unit]);

  const generateMarks = (prices: number[]): Record<number, React.ReactNode> => {
    const marks: Record<number, React.ReactNode> = {};

    prices.forEach((priceItem: number, index: number) => {
      const label = Helpers.formatNumberInput(priceItem.toFixed(2));

      let style: React.CSSProperties = {
        color: "#666",
        fontWeight: 400,
        fontSize: 12,
      };

      if (index === 1 || index === 2 || index === 3) {
        style = {
          ...style,
          color: "#378440",
          fontWeight: 600,
          fontSize: 12,
        };
      }

      marks[priceItem] = (
        <div className="" style={style}>
          {index === 0 && <div className="mark-tooltip">Min</div>}
          {index === 1 && (
            <div className="mark-tooltip">
              <Tooltip
                placement="topLeft"
                arrow={false}
                title={
                  <span style={{ whiteSpace: "pre-line" }}>
                    <b>${label}</b>
                    <span style={{ fontStyle: "italic" }}>
                      {"\n"} Quartile 1 (Q1): Low Price point (25% of market
                      prices are lower than or equal to this)
                    </span>
                  </span>
                }
              >
                Q1
              </Tooltip>
            </div>
          )}
          {index === 2 && (
            <div className="mark-tooltip q2-mark">
              <Tooltip
                placement="topLeft"
                arrow={false}
                title={
                  <span style={{ whiteSpace: "pre-line" }}>
                    <b>${label}</b>
                    <span style={{ fontStyle: "italic" }}>
                      {"\n"} Quartile 2 (Q2): Middle Price point (50% of market
                      prices are lower than or equal to this)
                    </span>
                  </span>
                }
              >
                Q2
              </Tooltip>
            </div>
          )}
          {index === 3 && (
            <div className="mark-tooltip">
              <Tooltip
                placement="topLeft"
                arrow={false}
                title={
                  <span style={{ whiteSpace: "pre-line" }}>
                    <b>${label}</b>
                    <span style={{ fontStyle: "italic" }}>
                      {"\n"} Quartile 3 (Q3): High Price point (75% of market
                      prices are lower than or equal to this)
                    </span>
                  </span>
                }
              >
                Q3
              </Tooltip>
            </div>
          )}
          {index === 4 && <div className="mark-tooltip">Max</div>}
        </div>
      );
    });

    return marks;
  };

  return (
    <div
      className="PriceSlider"
      style={
        {
          "--slider-height": `${type == "myProduct" ? "10px" : "40px"}`,
          "--slider-desc-margin-top": `${
            type == "myProduct" ? "30px" : "80px"
          }`,
          "--slider-mark-margin-top": `${
            type == "myProduct" ? "20px" : "50px"
          }`,
          "--lower1-percentage": `${metricPrices.lowerPercentage - 3}%`,
          "--lower2-percentage": `${metricPrices.lowerPercentage + 5}%`,
          "--upper1-percentage": `${metricPrices.upperPercentage - 3}%`,
          "--upper2-percentage": `${metricPrices.upperPercentage + 5}%`,
          "--slider-position-start": `${percentageRangesStart}%`,
          "--slider-position-end": `${percentageRangesEnd}%`,
          "--start-width": `${startRangeWidth}px`,
          "--start-position": `-${startRangeWidth}px`,
          "--end-width": `${endRangeWidth}px`,
          "--end-position": `-${endRangeWidth}px`,
        } as CustomStyle
      }
    >
      <Slider
        className="price-slider-component"
        marks={marks}
        included={false}
        value={
          unit == 1 ? productForm.price_per_tons : productForm.price_per_bale
        }
        min={metricPrices.minSliderPerTon - metricPrices.minSliderPerTon / 10}
        max={metricPrices.maxSliderPerTon + metricPrices.minSliderPerTon / 10}
        step={0.001}
      />
      <div className="slider-desc">
        <div className="desc-item">
          <div className="desc-item-dot"></div>
          <div className="desc-item-text">
            Market Min: $
            {Helpers.formatNumberInput(
              metricPrices.minSliderPerTon?.toFixed(2)
            )}
          </div>
        </div>
        <div className="desc-item">
          <div className="desc-item-dot"></div>
          <div className="desc-item-text">
            Average: $
            {Helpers.formatNumberInput(metricPrices.average?.toFixed(2))}
          </div>
        </div>
        <div className="desc-item">
          <div className="desc-item-dot"></div>
          <div className="desc-item-text">
            Market Max: $
            {Helpers.formatNumberInput(
              metricPrices.maxSliderPerTon?.toFixed(2)
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceSlider;
